import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type VoiceLanguage = 'it-IT' | 'en-US' | 'en-GB' | 'es-ES' | 'fr-FR' | 'de-DE';

export interface VoiceCommand {
  id: string;
  phrases: string[];
  description: string;
  action: string;
  context?: string[];
}

interface VoiceState {
  // Status
  isListening: boolean;
  isSpeaking: boolean;
  isProcessing: boolean;
  isEnabled: boolean;
  
  // Content
  transcript: string;
  lastCommand: VoiceCommand | null;
  error: string | null;
  
  // Settings
  language: VoiceLanguage;
  speechRate: number;
  speechPitch: number;
  speechVolume: number;
  autoListen: boolean;
  feedbackEnabled: boolean;
  
  // Context
  currentContext: string[];
  availableCommands: VoiceCommand[];
}

interface VoiceActions {
  // Status actions
  setIsListening: (listening: boolean) => void;
  setIsSpeaking: (speaking: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setEnabled: (enabled: boolean) => void;
  
  // Content actions
  setTranscript: (transcript: string) => void;
  setLastCommand: (command: VoiceCommand | null) => void;
  setError: (error: string | null) => void;
  clearTranscript: () => void;
  
  // Settings actions
  setLanguage: (language: VoiceLanguage) => void;
  setSpeechRate: (rate: number) => void;
  setSpeechPitch: (pitch: number) => void;
  setSpeechVolume: (volume: number) => void;
  setAutoListen: (auto: boolean) => void;
  setFeedbackEnabled: (enabled: boolean) => void;
  
  // Context actions
  setContext: (context: string[]) => void;
  addContext: (context: string) => void;
  removeContext: (context: string) => void;
  
  // Command actions
  registerCommand: (command: VoiceCommand) => void;
  unregisterCommand: (commandId: string) => void;
  registerCommands: (commands: VoiceCommand[]) => void;
}

type VoiceStore = VoiceState & VoiceActions;

// Default chess voice commands - FIXED UTF-8 encoding
const defaultCommands: VoiceCommand[] = [
  {
    id: 'next_move',
    phrases: ['prossima mossa', 'avanti', 'next move', 'forward'],
    description: 'Vai alla mossa successiva',
    action: 'NEXT_MOVE',
    context: ['analysis'],
  },
  {
    id: 'previous_move',
    phrases: ['mossa precedente', 'indietro', 'previous move', 'back'],
    description: 'Vai alla mossa precedente',
    action: 'PREVIOUS_MOVE',
    context: ['analysis'],
  },
  {
    id: 'go_start',
    phrases: ['inizio', 'vai all\'inizio', 'start', 'go to start'],
    description: 'Vai all\'inizio della partita',
    action: 'GO_START',
    context: ['analysis'],
  },
  {
    id: 'go_end',
    phrases: ['fine', 'vai alla fine', 'end', 'go to end'],
    description: 'Vai alla fine della partita',
    action: 'GO_END',
    context: ['analysis'],
  },
  {
    id: 'flip_board',
    phrases: ['gira scacchiera', 'flip board', 'ruota'],
    description: 'Ruota la scacchiera',
    action: 'FLIP_BOARD',
    context: ['analysis', 'training'],
  },
  {
    id: 'explain_move',
    phrases: ['spiega mossa', 'perché questa mossa', 'explain move', 'why this move'],
    description: 'Spiega la mossa corrente',
    action: 'EXPLAIN_MOVE',
    context: ['analysis'],
  },
  {
    id: 'best_move',
    phrases: ['mossa migliore', 'qual è la mossa migliore', 'best move'],
    description: 'Mostra la mossa migliore',
    action: 'BEST_MOVE',
    context: ['analysis', 'training'],
  },
  {
    id: 'help',
    phrases: ['aiuto', 'help', 'cosa posso dire', 'comandi'],
    description: 'Mostra i comandi disponibili',
    action: 'SHOW_HELP',
    context: ['all'],
  },
  {
    id: 'stop',
    phrases: ['stop', 'fermati', 'basta', 'silenzio'],
    description: 'Interrompi la sintesi vocale',
    action: 'STOP_SPEAKING',
    context: ['all'],
  },
];

const initialState: VoiceState = {
  isListening: false,
  isSpeaking: false,
  isProcessing: false,
  isEnabled: true,
  transcript: '',
  lastCommand: null,
  error: null,
  language: 'it-IT',
  speechRate: 1.0,
  speechPitch: 1.0,
  speechVolume: 1.0,
  autoListen: false,
  feedbackEnabled: true,
  currentContext: ['home'],
  availableCommands: defaultCommands,
};

export const useVoiceStore = create<VoiceStore>()(
  persist(
    immer((set) => ({
      ...initialState,

      setIsListening: (listening: boolean) => {
        set((state) => {
          state.isListening = listening;
        });
      },

      setIsSpeaking: (speaking: boolean) => {
        set((state) => {
          state.isSpeaking = speaking;
        });
      },

      setIsProcessing: (processing: boolean) => {
        set((state) => {
          state.isProcessing = processing;
        });
      },

      setEnabled: (enabled: boolean) => {
        set((state) => {
          state.isEnabled = enabled;
        });
      },

      setTranscript: (transcript: string) => {
        set((state) => {
          state.transcript = transcript;
        });
      },

      setLastCommand: (command: VoiceCommand | null) => {
        set((state) => {
          state.lastCommand = command;
        });
      },

      setError: (error: string | null) => {
        set((state) => {
          state.error = error;
        });
      },

      clearTranscript: () => {
        set((state) => {
          state.transcript = '';
        });
      },

      setLanguage: (language: VoiceLanguage) => {
        set((state) => {
          state.language = language;
        });
      },

      setSpeechRate: (rate: number) => {
        set((state) => {
          state.speechRate = Math.max(0.5, Math.min(2.0, rate));
        });
      },

      setSpeechPitch: (pitch: number) => {
        set((state) => {
          state.speechPitch = Math.max(0.5, Math.min(2.0, pitch));
        });
      },

      setSpeechVolume: (volume: number) => {
        set((state) => {
          state.speechVolume = Math.max(0, Math.min(1.0, volume));
        });
      },

      setAutoListen: (auto: boolean) => {
        set((state) => {
          state.autoListen = auto;
        });
      },

      setFeedbackEnabled: (enabled: boolean) => {
        set((state) => {
          state.feedbackEnabled = enabled;
        });
      },

      setContext: (context: string[]) => {
        set((state) => {
          state.currentContext = context;
        });
      },

      addContext: (context: string) => {
        set((state) => {
          if (!state.currentContext.includes(context)) {
            state.currentContext.push(context);
          }
        });
      },

      removeContext: (context: string) => {
        set((state) => {
          state.currentContext = state.currentContext.filter((c) => c !== context);
        });
      },

      registerCommand: (command: VoiceCommand) => {
        set((state) => {
          const existingIndex = state.availableCommands.findIndex(
            (c) => c.id === command.id
          );
          if (existingIndex >= 0) {
            state.availableCommands[existingIndex] = command;
          } else {
            state.availableCommands.push(command);
          }
        });
      },

      unregisterCommand: (commandId: string) => {
        set((state) => {
          state.availableCommands = state.availableCommands.filter(
            (c) => c.id !== commandId
          );
        });
      },

      registerCommands: (commands: VoiceCommand[]) => {
        set((state) => {
          commands.forEach((command) => {
            const existingIndex = state.availableCommands.findIndex(
              (c) => c.id === command.id
            );
            if (existingIndex >= 0) {
              state.availableCommands[existingIndex] = command;
            } else {
              state.availableCommands.push(command);
            }
          });
        });
      },
    })),
    {
      name: 'chessmaestro-voice',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isEnabled: state.isEnabled,
        language: state.language,
        speechRate: state.speechRate,
        speechPitch: state.speechPitch,
        speechVolume: state.speechVolume,
        autoListen: state.autoListen,
        feedbackEnabled: state.feedbackEnabled,
      }),
    }
  )
);

// Selectors
export const useVoiceStatus = () =>
  useVoiceStore((state) => ({
    isListening: state.isListening,
    isSpeaking: state.isSpeaking,
    isProcessing: state.isProcessing,
    isEnabled: state.isEnabled,
  }));

export const useVoiceSettings = () =>
  useVoiceStore((state) => ({
    language: state.language,
    speechRate: state.speechRate,
    speechPitch: state.speechPitch,
    speechVolume: state.speechVolume,
    autoListen: state.autoListen,
    feedbackEnabled: state.feedbackEnabled,
  }));

export const useVoiceTranscript = () => useVoiceStore((state) => state.transcript);
export const useVoiceError = () => useVoiceStore((state) => state.error);
